import React, { useState, useEffect } from 'react';
import type { Project, Settings } from '../types';
import AdminProjectFormPage from '../pages/admin/AdminProjectFormPage';

interface ProjectEditModalProps {
    mode: 'edit' | 'new';
    project?: Project;
    settings: Settings;
    onClose: () => void;
    onSaved: () => void;
}

const XIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;

const ProjectEditModal: React.FC<ProjectEditModalProps> = ({ mode, project, settings, onClose, onSaved }) => {
    const [isClosing, setIsClosing] = useState(false);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
        }, 300); // Match animation duration
    };
    
    // Close on escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);
    
    const title = mode === 'edit' ? 'Live Edit Project' : 'Add New Project';
    const subTitle = mode === 'edit' ? `Editing: ${project?.title}` : 'Fill in the details for the new portfolio entry.';

    return (
        <div 
            className={`fixed inset-0 bg-black/60 z-[100] flex justify-center items-start p-4 overflow-y-auto transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'}`}
            onClick={handleClose}
            aria-modal="true" 
            role="dialog"
        >
            <div 
                className={`w-full max-w-5xl bg-gray-100 rounded-lg shadow-2xl my-auto transform transition-all duration-300 ${isClosing ? 'scale-95' : 'scale-100'}`}
                onClick={e => e.stopPropagation()}
            >
                <div className="relative p-6 border-b bg-white rounded-t-lg">
                    <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
                    <p className="text-sm text-gray-500 truncate">{subTitle}</p>
                    <button 
                        onClick={handleClose}
                        className="absolute top-4 right-4 p-2 text-gray-500 hover:bg-gray-100 rounded-full"
                    >
                        <XIcon className="w-6 h-6"/>
                    </button>
                </div>
                <div className="p-6 max-h-[80vh] overflow-y-auto">
                    <AdminProjectFormPage 
                        key={project?.id || 'new-project'}
                        settings={settings}
                        isModalMode={true}
                        initialProject={project}
                        onModalSave={onSaved}
                        onModalClose={handleClose}
                    />
                </div>
            </div>
        </div>
    );
};

export default ProjectEditModal;